
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { images } from '../assets/images';
import { ArrowRight, ArrowLeft, Plus, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PortfolioPage = () => {
  const { id } = useParams();
  const [activeCategory, setActiveCategory] = useState('all');
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const categories = ['all', 'residential', 'commercial'];
  
  const filteredProjects = activeCategory === 'all' 
    ? images.portfolio
    : images.portfolio.filter(project => 
        project.category.toLowerCase() === activeCategory.toLowerCase()
      );
  
  const selectedProject = id ? images.portfolio.find(p => p.id === Number(id)) : null;
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleImageError = (src: string) => {
    console.error(`Failed to load portfolio image: ${src}`);
    setImageErrors(prev => ({...prev, [src]: true}));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-20 bg-pkgreen-100 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-pkgreen-600 font-display uppercase tracking-wider text-sm mb-2 block animate-fade-up">
              Our Work
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 text-gray-900 animate-fade-up">
              {selectedProject ? selectedProject.title : "Project Portfolio"}
            </h1>
            <p className="text-xl text-gray-700 animate-fade-up">
              {selectedProject 
                ? `${selectedProject.location} | ${selectedProject.category} Project` 
                : "Browse our collection of landscape transformations throughout Fairfield County."}
            </p>
          </div>
        </div>
      </section>
      
      {/* Portfolio Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {selectedProject ? (
              <div className="animate-fade-up">
                <div className="flex mb-8">
                  <Link to="/portfolio" className="inline-flex items-center text-pkgreen-600 hover:text-pkgreen-700">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Portfolio
                  </Link>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="rounded-lg overflow-hidden shadow-lg h-[400px] lg:h-[500px]">
                    {imageErrors[selectedProject.image] ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <div className="text-center p-4">
                          <AlertCircle className="h-12 w-12 text-gray-500 mx-auto mb-2" />
                          <p className="text-gray-500">Image unavailable</p>
                        </div>
                      </div>
                    ) : (
                      <img 
                        src={selectedProject.image} 
                        alt={selectedProject.title}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(selectedProject.image)}
                      />
                    )}
                  </div>
                  <div className="flex flex-col justify-between">
                    <div>
                      <span className="inline-block bg-pkgreen-100 text-pkgreen-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                        {selectedProject.category}
                      </span>
                      <h2 className="text-3xl font-serif font-bold mb-4">{selectedProject.title}</h2>
                      <p className="text-lg text-gray-700 mb-6">
                        {selectedProject.description || "This project showcases our commitment to quality landscaping, innovative design, and attention to detail."}
                      </p>
                      <div className="space-y-4 mb-6">
                        <div>
                          <h3 className="font-medium text-gray-900">Location</h3>
                          <p>{selectedProject.location}</p>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">Project Type</h3>
                          <p>{selectedProject.category}</p>
                        </div>
                      </div>
                    </div>
                    <Link
                      to="/contact"
                      className="inline-flex items-center bg-pkgreen-600 hover:bg-pkgreen-700 text-white py-3 px-8 rounded-md transition-all duration-300 w-fit mt-4"
                    >
                      Request a Similar Project
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </div>
                </div>
                
                {/* Before/After Section */}
                {selectedProject.beforeImage && (
                  <div className="mt-16 mb-16">
                    <h3 className="text-2xl font-serif font-bold mb-8 text-center">Before & After</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="rounded-lg overflow-hidden shadow-md">
                        <div className="h-64 md:h-80">
                          {imageErrors[selectedProject.beforeImage] ? (
                            <div className="w-full h-full flex items-center justify-center bg-gray-200">
                              <AlertCircle className="h-12 w-12 text-gray-500" />
                            </div>
                          ) : (
                            <img 
                              src={selectedProject.beforeImage} 
                              alt={`Before - ${selectedProject.title}`}
                              className="w-full h-full object-cover"
                              onError={() => handleImageError(selectedProject.beforeImage)}
                            />
                          )}
                        </div>
                        <div className="p-4 bg-pkstone-100">
                          <p className="font-medium text-center">Before</p>
                        </div>
                      </div>
                      <div className="rounded-lg overflow-hidden shadow-md">
                        <div className="h-64 md:h-80">
                          {imageErrors[selectedProject.image] ? (
                            <div className="w-full h-full flex items-center justify-center bg-gray-200">
                              <AlertCircle className="h-12 w-12 text-gray-500" />
                            </div>
                          ) : (
                            <img 
                              src={selectedProject.image} 
                              alt={`After - ${selectedProject.title}`}
                              className="w-full h-full object-cover"
                              onError={() => handleImageError(selectedProject.image)}
                            />
                          )}
                        </div>
                        <div className="p-4 bg-pkgreen-100">
                          <p className="font-medium text-center">After</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Related Projects */}
                <div className="mt-20">
                  <h3 className="text-2xl font-serif font-bold mb-8">Other Projects You Might Like</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {images.portfolio
                      .filter(p => p.id !== selectedProject.id && p.category === selectedProject.category)
                      .slice(0, 3)
                      .map(project => (
                        <div key={project.id} className="group rounded-lg overflow-hidden shadow-md bg-white">
                          <div className="relative overflow-hidden h-48">
                            {imageErrors[project.image] ? (
                              <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                <AlertCircle className="h-8 w-8 text-gray-500" />
                              </div>
                            ) : (
                              <img
                                src={project.image}
                                alt={project.title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                onError={() => handleImageError(project.image)}
                              />
                            )}
                            <div className="absolute inset-0 bg-pkgreen-950/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <Link 
                                to={`/portfolio/${project.id}`}
                                className="bg-white/90 text-pkgreen-800 p-3 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-300"
                              >
                                <Plus className="h-6 w-6" />
                              </Link>
                            </div>
                          </div>
                          <div className="p-4">
                            <h3 className="font-serif text-lg font-semibold mb-1">{project.title}</h3>
                            <p className="text-pkstone-600 text-sm mb-2">{project.location}</p>
                            <Link
                              to={`/portfolio/${project.id}`}
                              className="inline-flex items-center text-pkgreen-600 text-sm font-medium hover:text-pkgreen-700"
                            >
                              View project
                              <ArrowRight className="ml-1 h-4 w-4" />
                            </Link>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-12">
                  <h2 className="text-3xl font-serif font-bold mb-6 text-gray-900">
                    Our Featured Projects
                  </h2>
                  <p className="text-lg text-gray-700 mb-10">
                    Browse our portfolio of landscape transformations throughout Fairfield County.
                  </p>
                  
                  {/* Category Filters */}
                  <div className="flex justify-center mb-12">
                    <div className="inline-flex bg-pkstone-100 rounded-lg p-1">
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => setActiveCategory(category)}
                          className={cn(
                            "px-6 py-2 rounded-md font-medium transition-all duration-300 capitalize",
                            activeCategory === category
                              ? "bg-white shadow-md text-pkgreen-600"
                              : "text-pkstone-700 hover:text-pkgreen-600"
                          )}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Portfolio grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredProjects.map((project) => (
                    <div
                      key={project.id}
                      className="group rounded-lg overflow-hidden shadow-md bg-white"
                    >
                      <div className="relative overflow-hidden h-64">
                        {imageErrors[project.image] ? (
                          <div className="w-full h-full flex items-center justify-center bg-gray-200">
                            <div className="text-center p-4">
                              <AlertCircle className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                              <p className="text-gray-500 text-sm">Image unavailable</p>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-700 ease-smooth group-hover:scale-105"
                            onError={() => handleImageError(project.image)}
                          />
                        )}
                        <div className="absolute inset-0 bg-pkgreen-950/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <Link 
                            to={`/portfolio/${project.id}`}
                            className="bg-white/90 backdrop-blur-sm text-pkgreen-800 p-3 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-300 ease-smooth"
                          >
                            <Plus className="h-6 w-6" />
                          </Link>
                        </div>
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/90 backdrop-blur-sm text-pkstone-800 text-sm px-3 py-1 rounded-full font-medium">
                            {project.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="font-serif text-xl font-semibold mb-2">{project.title}</h3>
                        <p className="text-pkstone-600 mb-3">{project.location}</p>
                        <Link
                          to={`/portfolio/${project.id}`}
                          className="inline-flex items-center text-pkgreen-600 font-medium hover:text-pkgreen-700 transition-colors"
                        >
                          View project
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PortfolioPage;
